// Journal.js
import React, { useState, useEffect } from "react";
import { db } from "./firebase";
import { doc, getDoc, setDoc, onSnapshot } from "firebase/firestore";
import Loader from "./Loader";
import Notification from "./Notification";
import { ChevronUp, ChevronDown } from "lucide-react";

function groupGoalsBySection(goals, sections) {
  // Dla kompatybilności – zachowaj układ sekcji znany z Goals.
  const map = {};
  goals.forEach(g => {
    if (!map[g.category]) map[g.category] = [];
    map[g.category].push([
      g.question,
      g.uid, // preferuj field
      g.entryType || "number"
    ]);
  });
  // Sekcje – w kolejności z goals.sections jeśli istnieje, potem alfabetycznie
  const secList = (sections && sections.length ? sections : Object.keys(map))
    .filter(sec => map[sec] && map[sec].length > 0);
  return secList.map(title => ({ title, fields: map[title] || [] }));
}

const promiseWithTimeout = (promise, timeoutMs) => {
  return new Promise((resolve, reject) => {
    const timer = setTimeout(() => {
      reject(new Error("timeout"));
    }, timeoutMs);
    promise
      .then((res) => {
        clearTimeout(timer);
        resolve(res);
      })
      .catch((err) => {
        clearTimeout(timer);
        reject(err);
      });
  });
};

function Journal() {
  const today = new Date().toLocaleDateString();
  const entryDocId = today.replace(/\//g, "-");
  const [formData, setFormData] = useState({});
  const [sections, setSections] = useState([]); // to będą już sekcje w formacie jak defaultSections
  const [isLoading, setIsLoading] = useState(false);
  const [notification, setNotification] = useState("");

  // Dynamiczne pobieranie szablonu z goals!
  useEffect(() => {
    async function loadTemplateFromGoals() {
      try {
        const goalsSnap = await getDoc(doc(db, "goals", "currentGoals"));
        if (goalsSnap.exists()) {
          const { goals = [], sections: goalSections = [] } = goalsSnap.data();
          // Zgrupuj cele wg sekcji, zachowując format defaultSections
          setSections(groupGoalsBySection(goals, goalSections));
        } else {
          setSections([]); // Jeśli nie ma goals, nie pokazuj nic
        }
      } catch (error) {
        setSections([]);
      }
    }
    loadTemplateFromGoals();
  }, []);

// Journal.js
useEffect(() => {
  const entryRef = doc(db, "entries", entryDocId);
  const unsub = onSnapshot(entryRef, (snap) => {
    if (snap.exists()) {
      setFormData(snap.data());
    } else {
      setFormData({});
    }
  });
  return () => unsub();
}, [entryDocId]);



  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };
// Zwraca nowy obiekt z wszystkimi checkboxami ustawionymi na false jeśli nie istnieją
function normalizeCheckboxes(formData, sections) {
  const data = { ...formData };
  // Przejdź przez WSZYSTKIE pola w sekcjach
  sections.forEach(section => {
    section.fields.forEach(field => {
      let name, type;
      if (Array.isArray(field)) {
        name = field[1];
        type = field[2];
      } else {
        name = field.name;
        type = field.type;
      }
      if (type === "checkbox" && data[name] === undefined) {
        data[name] = false;
      }
    });
  });
  return data;
}

  const saveEntry = async () => {
    setIsLoading(true);
    try {
      const now = new Date();
      const normalizedFormData = normalizeCheckboxes(formData, sections);
const dataToSave = {
  ...normalizedFormData,
  date: today,
  updatedAt: now,
  createdAt: normalizedFormData.createdAt ? normalizedFormData.createdAt : now,
};

      await promiseWithTimeout(
        setDoc(doc(db, "entries", entryDocId), dataToSave, { merge: true }),
        10000
      );
      setNotification("Dane zapisane!");
    } catch (error) {
      setNotification("Błąd podczas zapisywania danych");
    } finally {
      setIsLoading(false);
      setTimeout(() => setNotification(""), 3000);
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  };

  // --- UI rendering jak miałeś ---
  return (
    <div className="px-4 py-6 max-w-full sm:max-w-2xl mx-auto">
      {isLoading && <Loader />}
      {notification && <Notification message={notification} />}
      <h1 className="text-2xl font-bold text-center">AI Journaling – {today}</h1>
      <style>{`
        table {
          width: 100%;
          border-collapse: collapse;
        }
        th, td {
          padding: 10px;
          vertical-align: middle;
        }
        .label-cell {
          width: 40%;
          text-align: left;
        }
        .input-cell {
          width: 60%;
          text-align: right;
        }
      `}</style>
      <div className="hidden sm:block overflow-x-auto">
        <table>
          <tbody>
            {sections.map((section) => (
              <React.Fragment key={section.title}>
                <tr>
                  <td colSpan="2" className="py-2">
                    <h3 className="text-xl font-bold text-gray-900 dark:text-white border-b pb-2">
                      {section.title}
                    </h3>
                  </td>
                </tr>
                {section.fields.map((field) => {
                  let label, name, type;
                  if (Array.isArray(field)) {
                    label = field[0];
                    name = field[1];
                    type = field[2];
                  } else {
                    label = field.label;
                    name = field.name;
                    type = field.type;
                  }
                  if (type === "textarea") {
                    return (
                      <tr key={name}>
                        <td className="label-cell py-2">{label}</td>
                        <td className="input-cell py-2">
                          <textarea
                            name={name}
                            onChange={handleChange}
                            value={formData[name] || ""}
                            rows="5"
                            title={label}
                            className="w-full p-2 min-h-[120px] resize-y bg-white border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-emerald-400 dark:bg-gray-800 dark:border-gray-600"
                          />
                        </td>
                      </tr>
                    );
                  } else if (type === "number") {
                    return (
                      <tr key={name}>
                        <td className="label-cell py-2">{label}</td>
                        <td className="input-cell py-2">
                          <div className="relative w-full sm:w-24 ml-auto">
                            <input
                              type="number"
                              name={name}
                              onChange={handleChange}
                              value={formData[name] || ""}
                              min="0"
                              title={label}
                              className="w-full p-2 pr-10 bg-white border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-emerald-400 dark:bg-gray-800 dark:border-gray-600"
                            />
                            <div className="absolute inset-y-0 right-0 flex flex-col items-center justify-center pr-1">
                              <button
                                type="button"
                                onClick={() => {
                                  const current = Number(formData[name] || 0);
                                  setFormData((prev) => ({ ...prev, [name]: current + 1 }));
                                }}
                                className="p-1 text-emerald-600 hover:text-emerald-800"
                              >
                                <ChevronUp size={16} />
                              </button>
                              <button
                                type="button"
                                onClick={() => {
                                  const current = Number(formData[name] || 0);
                                  if (current > 0) {
                                    setFormData((prev) => ({ ...prev, [name]: current - 1 }));
                                  }
                                }}
                                className="p-1 text-emerald-600 hover:text-emerald-800"
                              >
                                <ChevronDown size={16} />
                              </button>
                            </div>
                          </div>
                        </td>
                      </tr>
                    );
                  } else if (type === "checkbox") {
                    return (
                      <tr key={name}>
                        <td className="label-cell py-2">{label}</td>
                        <td className="input-cell py-2">
                          <input
                            type="checkbox"
                            name={name}
                            onChange={handleChange}
                            checked={formData[name] || false}
                            title={label}
                            className="transform scale-125"
                            style={{ accentColor: "#FFD700" }}
                          />
                        </td>
                      </tr>
                    );
                  }
                  return null;
                })}
              </React.Fragment>
            ))}
          </tbody>
        </table>
      </div>
      {/* Mobile */}
      <div className="block sm:hidden">
        {sections.map((section) => (
          <div key={section.title} className="mb-6">
            <h3 className="text-lg font-bold text-gray-900 dark:text-white border-b pb-2 mb-2">
              {section.title}
            </h3>
            {section.fields.map((field) => {
              let label, name, type;
              if (Array.isArray(field)) {
                label = field[0];
                name = field[1];
                type = field[2];
              } else {
                label = field.label;
                name = field.name;
                type = field.type;
              }
              if (type === "textarea") {
                return (
                  <div key={name} className="mb-4">
                    <label className="block text-gray-800 dark:text-gray-200 font-medium mb-1">
                      {label}
                    </label>
                    <textarea
                      name={name}
                      onChange={handleChange}
                      value={formData[name] || ""}
                      rows="5"
                      title={label}
                      className="w-full p-2 min-h-[120px] resize-y bg-white border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-emerald-400 dark:bg-gray-800 dark:border-gray-600"
                    />
                  </div>
                );
              } else {
                return (
                  <div key={name} className="mb-4 flex items-center">
                    <div className="w-1/3 min-w-[120px] text-gray-800 dark:text-gray-200 font-medium">
                      {label}
                    </div>
                    <div className="w-2/3">
                      {type === "number" ? (
                        <div className="relative">
                          <input
                            type="number"
                            name={name}
                            onChange={handleChange}
                            value={formData[name] || ""}
                            min="0"
                            title={label}
                            className="w-full p-2 pr-6 bg-white border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-emerald-400 dark:bg-gray-800 dark:border-gray-600"
                          />
                          <div className="absolute inset-y-0 right-0 flex flex-col items-center justify-center pr-1">
                            <button
                              type="button"
                              onClick={() => {
                                const current = Number(formData[name] || 0);
                                setFormData((prev) => ({ ...prev, [name]: current + 1 }));
                              }}
                              className="p-1 text-emerald-600 hover:text-emerald-800"
                            >
                              <ChevronUp size={14} />
                            </button>
                            <button
                              type="button"
                              onClick={() => {
                                const current = Number(formData[name] || 0);
                                if (current > 0) {
                                  setFormData((prev) => ({ ...prev, [name]: current - 1 }));
                                }
                              }}
                              className="p-1 text-emerald-600 hover:text-emerald-800"
                            >
                              <ChevronDown size={14} />
                            </button>
                          </div>
                        </div>
                      ) : type === "checkbox" ? (
                        <div className="flex justify-end">
                          <input
                            type="checkbox"
                            name={name}
                            onChange={handleChange}
                            checked={formData[name] || false}
                            title={label}
                            className="transform scale-125"
                            style={{ accentColor: "#FFD700" }}
                          />
                        </div>
                      ) : null}
                    </div>
                  </div>
                );
              }
            })}
          </div>
        ))}
      </div>
      <br />
      <div className="sticky bottom-0 left-0 right-0 bg-white/70 dark:bg-gray-800/70 p-4 flex justify-center items-center z-50">
        <button
          onClick={saveEntry}
          className="bg-emerald-500 text-white px-6 py-2 rounded-lg hover:bg-emerald-600 transition border-2 border-green-500 focus:outline-none"
        >
          Zapisz
        </button>
      </div>
    </div>
  );
}

export default Journal;
